import type { LiteratureDto } from '~/utils/solr/literature'
import { SolrParamsKey } from '~~/shared/solr'
import { createCountWithFilters } from '~/utils/filter-count'
import { extractLabel, extractName, getLastName, processFacets } from '#shared/utils/solr-facet'
import { buildEditionFamilyMap, sortLiteratureCatalogue } from '~/utils/literature/catalogue-sort'
import { toLiteratureDto } from '~/utils/literature/catalogue-item'
import { compareLocaleNorwegian } from '~/utils/string'
import { useBookAndArticleQuery } from './useBookAndArticleQuery'

export interface LiteratureFilterState {
  searchKeyword: string
  showOnlyFavorites: boolean
  typeFilter: { value: string; label: string }[]
  legalAreaFilter: { value: string; label: string }[]
  authorFilter: { value: string; label: string }[]
  sortFilter: { value: string; label: string }
}

type LiteratureDrawerOverrides = Omit<LiteratureFilterState, 'searchKeyword' | 'sortFilter'>

// Keep these comparison keys aligned with the facet config so counts match the rendered list.
export function literatureMatches(item: LiteratureDto, state: LiteratureFilterState): boolean {
  if (
    !matchesAllSearchTerms(state.searchKeyword, [item.title, ...item.authors, ...item.legalAreas])
  ) {
    return false
  }

  if (state.typeFilter.length > 0) {
    const selectedNodeTypes = state.typeFilter.map((f) =>
      f.value === 'book' ? 'book' : 'academic_article',
    )
    if (!selectedNodeTypes.includes(item.nodeType)) {
      return false
    }
  }

  if (state.legalAreaFilter.length > 0) {
    const itemAreas = new Set(item.legalAreas.map((area) => area.toLowerCase()))
    if (!state.legalAreaFilter.some((f) => itemAreas.has(extractLabel(f.value).toLowerCase()))) {
      return false
    }
  }

  if (state.authorFilter.length > 0) {
    const selectedAuthors = new Set(
      state.authorFilter.map((f) => extractName(f.value).toLowerCase()),
    )
    if (!item.authors.some((author) => selectedAuthors.has(author.toLowerCase()))) {
      return false
    }
  }

  if (state.showOnlyFavorites && !item.isFavorite) {
    return false
  }
  return true
}

export const LITERATURE_FACET_CONFIG = {
  typeFilter: {
    docKeys: (doc: LiteratureDto) => [doc.nodeType],
    optionKey: (value: string) => (value === 'book' ? 'book' : 'academic_article'),
  },
  legalAreaFilter: {
    docKeys: (doc: LiteratureDto) => doc.legalAreas.map((area) => area.toLowerCase()),
    optionKey: (value: string) => extractLabel(value).toLowerCase(),
  },
  authorFilter: {
    docKeys: (doc: LiteratureDto) => doc.authors.map((author) => author.toLowerCase()),
    optionKey: (value: string) => extractName(value).toLowerCase(),
  },
} as const satisfies Partial<
  Record<FacetKeys<LiteratureFilterState>, FacetIndexConfig<LiteratureDto>>
>

const LITERATURE_TYPE_OPTIONS = [
  { value: 'book', label: NODE_TYPE_DISPLAY_LABELS.book },
  { value: 'academic_article', label: NODE_TYPE_DISPLAY_LABELS.academic_article },
]

type FacetValueCount = { value: string; count: number }

function useLiteratureFilterState() {
  const searchKeyword = useState<string>('literature-filter-search', () => '')
  const showOnlyFavorites = useState<boolean>('literature-filter-favorites', () => false)
  const typeFilter = useState<{ value: string; label: string }[]>(
    'literature-filter-type',
    () => [],
  )
  const legalAreaFilter = useState<{ value: string; label: string }[]>(
    'literature-filter-legal-area',
    () => [],
  )
  const authorFilter = useState<{ value: string; label: string }[]>(
    'literature-filter-author',
    () => [],
  )
  const sortFilter = useState<{ value: string; label: string }>(
    'literature-filter-sort',
    () => LITERATURE_SORTING_OPTIONS[0],
  )
  const searchLegalArea = ref<string>('')
  const searchAuthor = ref<string>('')

  return {
    searchKeyword,
    showOnlyFavorites,
    typeFilter,
    legalAreaFilter,
    authorFilter,
    sortFilter,
    searchLegalArea,
    searchAuthor,
  }
}

function useLiteratureSource() {
  const { isFavorite } = useFavorites()
  const {
    data: solrData,
    isLoading,
    suspense,
  } = useBookAndArticleQuery({ [SolrParamsKey.ContentType]: ['book', 'academic_article'] })

  return { solrData, isLoading, suspense, isFavorite }
}

function buildTypeOptions(facets: FacetValueCount[] | null | undefined) {
  const available = new Set(
    (facets ?? []).filter((item) => item.count > 0).map((item) => item.value),
  )
  return LITERATURE_TYPE_OPTIONS.filter((option) => available.has(option.value))
}

function buildLegalAreaOptions(facets: FacetValueCount[] | null | undefined) {
  return processFacets(facets ?? [], (item) => ({
    value: item.value,
    label: extractLabel(item.value),
  })).sort((a, b) => compareLocaleNorwegian(a.label, b.label))
}

function buildAuthorOptions(authorLists: FacetValueCount[], authors: FacetValueCount[]) {
  const byName = new Map<string, FacetValueCount>()
  for (const facet of [...authorLists, ...authors]) {
    const name = extractName(facet.value)
    const existing = byName.get(name)
    byName.set(name, { value: facet.value, count: (existing?.count || 0) + facet.count })
  }
  return processFacets([...byName.values()], (item) => ({
    value: item.value,
    label: extractName(item.value),
  })).sort((a, b) => {
    const lastNameA = getLastName(a.label).toLowerCase()
    const lastNameB = getLastName(b.label).toLowerCase()
    return compareLocaleNorwegian(lastNameA, lastNameB)
  })
}

function useLiteratureOptions(source: ReturnType<typeof useLiteratureSource>) {
  const fields = computed(() => source.solrData.value?.facet_counts?.facet_fields)
  const typeOptions = computed(() => buildTypeOptions(fields.value?.nodeType))
  const legalAreaOptions = computed(() => buildLegalAreaOptions(fields.value?.legal_areas))
  const authorOptions = computed(() =>
    buildAuthorOptions(fields.value?.author_list ?? [], fields.value?.author ?? []),
  )
  return { typeOptions, legalAreaOptions, authorOptions }
}

function hasLiteratureFilters(state: ReturnType<typeof useLiteratureFilterState>): boolean {
  return Boolean(
    state.searchKeyword.value.length ||
    state.showOnlyFavorites.value ||
    state.typeFilter.value.length ||
    state.legalAreaFilter.value.length ||
    state.authorFilter.value.length,
  )
}

function useLiteratureResults(
  source: ReturnType<typeof useLiteratureSource>,
  state: ReturnType<typeof useLiteratureFilterState>,
) {
  const transformedLiteratureData = computed<LiteratureDto[]>(() => {
    return (source.solrData.value?.response.docs ?? []).map((item) =>
      toLiteratureDto(item, source.isFavorite('literature', item.id)),
    )
  })
  const editionFamilies = computed(() => buildEditionFamilyMap(transformedLiteratureData.value))
  const committedState = computed<LiteratureFilterState>(() => ({
    searchKeyword: state.searchKeyword.value,
    showOnlyFavorites: state.showOnlyFavorites.value,
    typeFilter: state.typeFilter.value,
    legalAreaFilter: state.legalAreaFilter.value,
    authorFilter: state.authorFilter.value,
    sortFilter: state.sortFilter.value,
  }))
  const filteredLiterature = computed<LiteratureDto[]>(() => {
    return transformedLiteratureData.value.filter((item) =>
      literatureMatches(item, committedState.value),
    )
  })
  const totalHits = computed<number>(() => filteredLiterature.value.length)
  const sortedFilteredLiterature = computed<LiteratureDto[]>(() => {
    return sortLiteratureCatalogue(
      filteredLiterature.value,
      state.searchKeyword.value,
      state.sortFilter.value.value,
      editionFamilies.value,
    )
  })
  const hasFilters = computed(() => hasLiteratureFilters(state))

  return {
    transformedLiteratureData,
    committedState,
    filteredLiterature,
    sortedFilteredLiterature,
    totalHits,
    hasFilters,
  }
}

function useLiteratureFacets(
  state: ReturnType<typeof useLiteratureFilterState>,
  options: ReturnType<typeof useLiteratureOptions>,
  results: ReturnType<typeof useLiteratureResults>,
) {
  const countForFacet = createCountWithFilters(
    results.transformedLiteratureData,
    results.committedState,
    literatureMatches,
  )
  const counts = useFacetIndex(
    results.transformedLiteratureData,
    results.committedState,
    literatureMatches,
    LITERATURE_FACET_CONFIG,
  )
  const type = useFacetFilter(state.typeFilter, options.typeOptions, counts.typeFilter)
  const legalArea = useFacetFilter(
    state.legalAreaFilter,
    options.legalAreaOptions,
    counts.legalAreaFilter,
  )
  const author = useFacetFilter(state.authorFilter, options.authorOptions, counts.authorFilter)

  return {
    typeSelected: type.selected,
    toggleType: type.toggle,
    typeFacetOptions: type.facetOptions,
    legalAreaSelected: legalArea.selected,
    toggleLegalArea: legalArea.toggle,
    legalAreaFacetOptions: legalArea.facetOptions,
    authorSelected: author.selected,
    toggleAuthor: author.toggle,
    authorFacetOptions: author.facetOptions,
    countWithFilters: (overrides: LiteratureDrawerOverrides) => countForFacet(overrides),
  }
}

function useLiteratureActions(state: ReturnType<typeof useLiteratureFilterState>) {
  const toggleFavorite = () => (state.showOnlyFavorites.value = !state.showOnlyFavorites.value)
  function clearFilters() {
    state.searchKeyword.value = ''
    state.showOnlyFavorites.value = false
    state.typeFilter.value = []
    state.legalAreaFilter.value = []
    state.authorFilter.value = []
    state.searchLegalArea.value = ''
    state.searchAuthor.value = ''
  }
  return { toggleFavorite, clearFilters }
}

function _useLiteratureFilters() {
  const state = useLiteratureFilterState()
  const source = useLiteratureSource()
  const options = useLiteratureOptions(source)
  const results = useLiteratureResults(source, state)
  const facets = useLiteratureFacets(state, options, results)

  return {
    ...state,
    ...options,
    ...facets,
    ...useLiteratureActions(state),
    totalHits: results.totalHits,
    filteredLiterature: results.filteredLiterature,
    sortedFilteredLiterature: results.sortedFilteredLiterature,
    hasFilters: results.hasFilters,
    isLoading: source.isLoading,
    suspense: source.suspense,
  }
}

export const useLiteratureFilters = createSharedComposable(_useLiteratureFilters)
